import { Link } from "react-router-dom";
import { Check, X, Wrench, Repeat, ChevronRight, Lightbulb } from "lucide-react";
import { formatDate } from "@/lib/api";

export const SECTIONS = [
  { key: "learned", label: "What you learned", color: "#A855F7", icon: Lightbulb },
  { key: "worked", label: "What worked", color: "#10B981", icon: Check },
  { key: "failed", label: "What failed", color: "#EF4444", icon: X },
  { key: "fix", label: "Fix this", color: "#F59E0B", icon: Wrench },
  { key: "drill", label: "Drill next", color: "#3B82F6", icon: Repeat },
];

export function TagChips({ tags, testPrefix = "tag" }) {
  if (!tags?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((t) => (
        <Link
          key={t.name}
          to={`/tag/${encodeURIComponent(t.name)}`}
          data-testid={`${testPrefix}-${t.name.replace(/\s+/g, "-")}`}
          className="chip"
        >
          <span className={`w-1.5 h-1.5 rounded-full ${t.type === "position" ? "bg-[#A1A1AA]" : t.type === "submission" ? "bg-[#E50914]" : "bg-white"}`} />
          {t.name}
        </Link>
      ))}
    </div>
  );
}

function Section({ s, items }) {
  const Icon = s.icon;
  return (
    <div
      data-testid={`section-${s.key}`}
      className="px-4 py-3.5 rounded-r-xl border-l-2"
      style={{ borderColor: s.color, background: `${s.color}0D` }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color: s.color }} strokeWidth={3} />
        <span className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: s.color }}>{s.label}</span>
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-[15px] leading-snug text-[#E4E4E7]">{it}</li>
        ))}
      </ul>
    </div>
  );
}

export function SessionCard({ session }) {
  const filled = SECTIONS.filter((s) => session[s.key]?.length);
  return (
    <article data-testid="session-card" className="card overflow-hidden rise">
      <div className="p-5 pb-4">
        <div className="eyebrow mb-1.5">{formatDate(session.local_date)}</div>
        <h2 className="font-display text-xl font-semibold uppercase tracking-tight" data-testid="session-title">{session.title}</h2>
        {session.summary && <p className="text-sm text-[#A1A1AA] mt-1.5 leading-relaxed">{session.summary}</p>}
      </div>
      <div className="space-y-2 pr-3">
        {filled.map((s) => <Section key={s.key} s={s} items={session[s.key]} />)}
      </div>
      {session.tags?.length > 0 && (
        <div className="p-5 pt-4">
          <div className="eyebrow mb-2.5">Came up</div>
          <TagChips tags={session.tags} />
        </div>
      )}
    </article>
  );
}

export function SessionRow({ session, index = 0 }) {
  return (
    <Link
      to={`/session/${session.id}`}
      data-testid={`session-row-${session.id}`}
      className="card p-4 flex items-center gap-4 rise hover:border-[#3F3F46] transition-colors duration-200 active:scale-[0.99]"
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      <div className="min-w-0 flex-1">
        <div className="text-xs text-[#71717A] mb-1">{formatDate(session.local_date)}</div>
        <div className="font-semibold truncate">{session.title}</div>
        <div className="flex items-center gap-3 mt-2">
          {SECTIONS.filter((s) => s.key !== "learned" || session.learned?.length).map((s) => (
            <span key={s.key} className="flex items-center gap-1 text-xs text-[#A1A1AA]">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
              {session[s.key]?.length || 0}
            </span>
          ))}
          {session.tags?.[0] && <span className="text-xs text-[#71717A] truncate capitalize">· {session.tags.slice(0, 2).map((t) => t.name).join(", ")}</span>}
        </div>
      </div>
      <ChevronRight size={18} className="text-[#52525B] shrink-0" />
    </Link>
  );
}
